import config from 'config';
import axiosClient from 'config/axios';
import { ListPayload, ListResponse, SuccessResponse } from 'core/http/response';
import {
    ICS2ArrivalCreationResult,
    ICS2ArrivalData,
    ICS2ArrivalHistoryEntry,
    ICS2ArrivalNotificationMessage,
    ICS2ArrivalStatus,
    ICS2ArrivalSubmitResult,
    ICS2ArrivalView,
} from './ics2-arrival-notification-declaration';

const baseUrl = () => `${config.declarationsUrl}/eu/ics2/arrival-notifications`;

export interface ListIcs2ArrivalsParams {
    page?: number;
    size?: number;
    status?: ICS2ArrivalStatus;
    lrn?: string;
    arrivalMrn?: string;
    relatedMrn?: string;
    from?: string;
    to?: string;
    signal?: AbortSignal;
    manifestId?: string;
}

export const listIcs2Arrivals = async ({ signal, ...params }: ListIcs2ArrivalsParams = {}): Promise<
    ListPayload<ICS2ArrivalView>
> => {
    const response = await axiosClient.get<ListResponse<ICS2ArrivalView>>(baseUrl(), {
        params: { page: 0, size: 20, ...params },
        signal,
    });

    return response.data.payload;
};

export const getIcs2Arrival = async (id: string): Promise<ICS2ArrivalView> => {
    const response = await axiosClient.get<SuccessResponse<ICS2ArrivalView>>(`${baseUrl()}/${id}`);
    return response.data.payload;
};

export const createIcs2ArrivalFromManifest = async (manifestId: string): Promise<ICS2ArrivalCreationResult> => {
    const response = await axiosClient.post<SuccessResponse<ICS2ArrivalCreationResult>>(`${baseUrl()}/from-manifest`, {
        manifestId,
    });
    return response.data.payload;
};

/**
 * Replaces the draft data outright. `expectedVersion` is the version the form was loaded from: the
 * service rejects the call with a 409 if the arrival moved on in the meantime, or if it has been
 * submitted and is no longer editable.
 */
export const updateIcs2Arrival = async (
    id: string,
    expectedVersion: number,
    data: ICS2ArrivalData
): Promise<ICS2ArrivalView> => {
    const response = await axiosClient.put<SuccessResponse<ICS2ArrivalView>>(`${baseUrl()}/${id}`, {
        expectedVersion,
        data,
    });

    return response.data.payload;
};

export const submitIcs2Arrival = async (
    id: string,
    expectedVersion: number,
    idempotencyKey: string
): Promise<ICS2ArrivalSubmitResult> => {
    const response = await axiosClient.post<SuccessResponse<ICS2ArrivalSubmitResult>>(
        `${baseUrl()}/${id}/submit`,
        { expectedVersion },
        { headers: { 'X-Idempotency-Key': idempotencyKey } }
    );

    return response.data.payload;
};

export const getIcs2ArrivalHistory = async (id: string): Promise<ICS2ArrivalHistoryEntry[]> => {
    const response = await axiosClient.get<SuccessResponse<ICS2ArrivalHistoryEntry[]>>(`${baseUrl()}/${id}/history`);
    return response.data.payload;
};

/** The arrival's message history: registrations, rejections, house-state problems and controls. */
export const listIcs2ArrivalMessages = async (
    id: string,
    params: { page?: number; size?: number } = {}
): Promise<ListPayload<ICS2ArrivalNotificationMessage>> => {
    const response = await axiosClient.get<ListResponse<ICS2ArrivalNotificationMessage>>(
        `${baseUrl()}/${id}/notifications`,
        { params: { page: 0, size: 20, ...params } }
    );

    return response.data.payload;
};
